<script setup>
  import { computed, ref, watch } from 'vue'
  import { SEmail, SLinkedIn } from 'vue-socials'
  import { useAccessibility } from '@/composables/accessibility'

  const open = ref(false)
  const { copyFeedback, copyToClipboard: handleCopyToClipboard } = useAccessibility()

  const currentUrl = computed(() => {
    return window.location.href
  })

  const linkedInOptions = computed(() => {
    return {
      url: currentUrl.value
    }
  })

  const copyToClipboard = async () => {
    await handleCopyToClipboard(currentUrl.value)
  }
</script>

<template>
  <div class="relative w-full lg:w-fit">
    <button
      class="share-links-trigger z-20 gap-2 text-body border border-primary border-opacity-40 bg-white flex flex-row items-center justify-center py-4 px-8 hover:border-opacity-100 transition-all duration-300 ease-in-out rounded-full w-full lg:w-fit"
      @click="open = !open"
      aria-label="Öffne die Optionen zum Teilen"
    >
      <AppIcon
        :name="open ? 'Close' : 'Share'"
      />
      Teilen
    </button>
    <Transition name="fade" mode="out-in">
      <div
        v-if="open"
        class="share-links-buttons absolute bottom-16 right-0 w-full"
      >
        <button
          class="share-links-button mb-1.5 ~h-10/12 w-full px-4 gap-2 rounded-full transition-opacity flex items-center justify-center z-20 bg-white border border-primary"
          :aria-label="copyFeedback ? 'Link kopiert!' : 'Link kopieren'"
          :aria-pressed="copyFeedback"
          @click="copyToClipboard"
          @keydown.enter="copyToClipboard"
          @keydown.space.prevent="copyToClipboard"
          title="Link in die Zwischenablage kopieren"
        >
          <AppIcon
            name="Copy"
          />
          Kopieren
        </button>
        <SLinkedIn
          class="share-links-button mb-1.5 ~h-10/12 w-full px-4 gap-2 rounded-full transition-opacity flex items-center justify-center z-20 bg-white border border-primary"
          :shareOptions="linkedInOptions"
        >
          <AppIcon
            name="Linkedin"
          />
          LinkedIn
        </SLinkedIn>
        <SEmail
          class="share-links-button ~h-10/12 w-full px-4 gap-2 rounded-full transition-opacity flex items-center justify-center z-20 bg-white border border-primary"
          :shareOptions="{
            subject: 'Sieh dir diese Veranstaltung an',
            body: `Hallo, schau dir diese Veranstaltung an: ${currentUrl}`,
          }"
        >
          <AppIcon
            name="Envelope"
          />
          E-Mail
        </SEmail>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity .3s ease;
  }
  .fade-enter-from, .fade-leave-to {
    opacity: 0;
  }

  .share-links-trigger :deep(svg) {
    @apply h-4 w-4 mt-0;
  }
  .share-links-button :deep(svg path) {
    fill: #C83528 !important;
  }

  // Screen reader only class for accessibility announcements
  :global(.sr-only) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
</style>
